import { createSelector } from 'reselect';

import { ONBOARDING_TOPPER_KEY, SCREENS } from '../../data/wordCityConstants';

// the level's finished tower, every floor it will have by the end of the round
export const getLevelTower = level => level.standardItems.map(item => item.targetLetters)
  .map(targetLetters => targetLetters.join(''))
  .reverse().concat(level.standardItems[0].referenceLetters.join(''));

// the tower as it stands right now, mid-round
export const getRoundTower = round => (round.currentStepIndex === null ?
  [round.standardItems[0].referenceLetters.join('')] :
  [round.standardItems[round.currentStepIndex].targetLetters.join(''), ...round.tower]);

export const getCity = ({ curriculum, gameDetails, feedback, onboardingRound, gameMode }) => {
  if (feedback.selectedTopperId === null) {
    return null;
  }

  const cityLevels = gameMode.isOnboardingMode ?
    [{ id: 0, standardItems: onboardingRound.standardItems }] :
    curriculum.levels.filter(
      level => gameDetails.gameDetails.toppers[level.id] !== undefined || level.id === curriculum.currentLevelId,
    );

  /* The districts are ordered by level, not by when they were built, so in practice the tower just
  raised can land anywhere in the row. isNewest names it rather than leaving the screen to guess from
  its position, which only ever worked while levels were played in order. */
  const districts = cityLevels.map((level) => {
    const isNewest = level.id === curriculum.currentLevelId || gameMode.isOnboardingMode;

    return {
      levelId: level.id,
      tower: getLevelTower(level),
      isNewest,
      topperId: isNewest ? feedback.selectedTopperId : gameDetails.gameDetails.toppers[level.id],
    };
  });

  /* Onboarding raised a tower too, and it is none of the curriculum's levels. Its words are fixed, so
  it is rebuilt from them rather than remembered, and it stands westernmost, having gone up first. The
  id only has to colour it and tell it apart in the row, so it takes one past the last level. */
  const onboardingTopperId = gameDetails.gameDetails.toppers[ONBOARDING_TOPPER_KEY];
  const hasOnboardingDistrict = !gameMode.isOnboardingMode &&
    onboardingTopperId !== undefined &&
    !!curriculum.onboardingItems.length;

  return hasOnboardingDistrict ? [{
    levelId: curriculum.levels.length,
    tower: getLevelTower({ standardItems: curriculum.onboardingItems }),
    isNewest: false,
    topperId: onboardingTopperId,
  }, ...districts] : districts;
};

export const getCurrentLevel = curriculum => curriculum.levels[curriculum.currentLevelId];

export const getCurrentStandardItem = standardRound => standardRound.standardItems[standardRound.currentStepIndex];

export const getCurrentOnboardingItem = onboardingRound => getCurrentStandardItem(onboardingRound);

export const isDoubleLetterStep = ({ referenceLetters }) =>
  !!referenceLetters.find(letter => letter.length > 1);

const getPercentage = (number, total) => Math.round(number / total * 100);

/* Floors standing in the round being played, onboarding included. Counted rather than derived from
the step index because it only ever grows: a finished round nulls its index, which would walk
anything keyed off it back to the start. */
export const selectTowerFloorCount = createSelector(
  [
    ({ standardRound }) => standardRound.tower.length,
    ({ onboardingRound }) => onboardingRound.tower.length,
    ({ gameMode }) => gameMode.isOnboardingMode,
  ],
  (standardFloorCount, onboardingFloorCount, isOnboardingMode) =>
    (isOnboardingMode ? onboardingFloorCount : standardFloorCount),
);

/* The round is over once the tower holds a floor per item on top of the reference word it started
on. Read off the tower, not the step index: the index nulls on the very tick the last floor lands, so
by the time anything renders it can no longer tell a finished round from one that never began. */
export const selectIsRoundComplete = createSelector(
  [
    ({ standardRound }) => standardRound,
    ({ onboardingRound }) => onboardingRound,
    ({ gameMode }) => gameMode.isOnboardingMode,
  ],
  (standardRound, onboardingRound, isOnboardingMode) => {
    /* Which round is running, told by the mode rather than the screen: the feedback screen belongs
    to whichever round just ended, so it cannot say on its own which one that was. */
    const round = isOnboardingMode ? onboardingRound : standardRound;

    return !!round.standardItems.length && round.tower.length === round.standardItems.length + 1;
  },
);

export const selectRoundProgressPercentage = createSelector(
  [
    ({ standardRound }) => standardRound.currentStepIndex,
    ({ standardRound }) => standardRound.standardItems.length,
    ({ onboardingRound }) => onboardingRound.standardItems.length,
    ({ onboardingRound }) => onboardingRound.currentStepIndex,
    ({ standardStep }) => standardStep.isAnswerCorrect,
    ({ screen }) => screen.currentScreen,
  ],
  (
    standardStepIndex,
    standardRoundLength,
    onboardingRoundLength,
    onboardingStepIndex,
    isAnswerCorrect,
    currentScreen,
  ) => {
    const isStandardScreen = currentScreen === SCREENS.STANDARD;
    const questionIndex = isStandardScreen ? standardStepIndex : onboardingStepIndex;
    const roundLength = isStandardScreen ? standardRoundLength : onboardingRoundLength;

    // a finished round nulls its index, and `null + 1` would walk the bar back to 1/N
    if (questionIndex === null) {
      return roundLength ? 100 : 0;
    }

    return getPercentage(isAnswerCorrect ? questionIndex + 1 : questionIndex, roundLength);
  },
);
